import { MessageSquare, Phone, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

/**
 * SMS Setup Dialog
 * Handles phone number entry and SMS verification
 */
export function SmsSetupDialog({
  open,
  onOpenChange,
  smsStep,
  setSmsStep,
  phoneNumber,
  setPhoneNumber,
  smsCode,
  setSmsCode,
  actionLoading,
  error,
  onSendSmsCode,
  onVerifySms
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-teal-600" />
            <span>Sett opp SMS-verifisering</span>
          </DialogTitle>
          <DialogDescription>
            {smsStep === 'phone' && 'Skriv inn telefonnummeret ditt'}
            {smsStep === 'verify' && 'Skriv inn koden du mottok på SMS'}
          </DialogDescription>
        </DialogHeader>

        {smsStep === 'phone' && (
          <PhoneEntryStep
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            actionLoading={actionLoading}
            error={error}
            onClose={() => onOpenChange(false)}
            onSendCode={onSendSmsCode}
          />
        )}

        {smsStep === 'verify' && (
          <SmsVerifyStep
            phoneNumber={phoneNumber}
            smsCode={smsCode}
            setSmsCode={setSmsCode}
            actionLoading={actionLoading}
            error={error}
            onBack={() => setSmsStep('phone')}
            onVerify={onVerifySms}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PhoneEntryStep({ phoneNumber, setPhoneNumber, actionLoading, error, onClose, onSendCode }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="phone-number">Telefonnummer</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            id="phone-number"
            placeholder="+47 123 45 678"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="pl-10"
          />
        </div>
        <p className="text-xs text-slate-500">
          Bruk internasjonalt format med landkode (f.eks. +47 for Norge)
        </p>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600">
          {error}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Avbryt
        </Button>
        <Button 
          onClick={onSendCode} 
          disabled={phoneNumber.length < 8 || actionLoading}
        >
          {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Send kode
        </Button>
      </DialogFooter>
    </div>
  );
}

function SmsVerifyStep({ phoneNumber, smsCode, setSmsCode, actionLoading, error, onBack, onVerify }) {
  return (
    <div className="space-y-4">
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
        <p className="text-sm text-teal-700">
          En verifiseringskode er sendt til <strong>{phoneNumber}</strong>
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="sms-code">Verifiseringskode</Label>
        <Input
          id="sms-code"
          placeholder="123456"
          value={smsCode}
          onChange={(e) => setSmsCode(e.target.value)}
          className="text-center text-2xl tracking-widest"
          maxLength={6}
        />
        <p className="text-xs text-slate-500">
          Skriv inn den 6-sifrede koden fra SMS-en
        </p>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600">
          {error}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          Tilbake
        </Button>
        <Button 
          onClick={onVerify} 
          disabled={smsCode.length < 6 || actionLoading}
        >
          {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Verifiser
        </Button>
      </DialogFooter>
    </div>
  );
}
